<template>
  <section class="status-toolbar" aria-label="Resumo e filtro por situação">
    <div class="status-summary" aria-live="polite" :aria-busy="statusLoading">
      <span class="status-caption">{{ summaryLabel }}</span>
      <span v-if="statusLoading" class="status-skeleton" aria-label="Carregando resumo"></span>
      <strong v-else class="status-total">{{ statusError ? '—' : formatCount(summaryCount) }}</strong>
      <small>{{ computedKpis.length }} situações disponíveis</small>
    </div>

    <div class="status-selector">
      <label :for="selectorId">Situação do pedido</label>
      <v-select
        :input-id="selectorId"
        :value="activeOption"
        :options="computedKpis"
        label="label"
        :disabled="disabled"
        :clearable="false"
        :filter="searchOptions"
        :selectable="isSelectable"
        placeholder="Buscar ou selecionar situação"
        @input="selectOption"
      >
        <template #option="option">
          <span class="status-option">
            <span>{{ option.label }}</span>
            <small>{{ option.count === null ? 'Sem contagem' : formatCount(option.count) }}</small>
          </span>
        </template>
        <template #no-options="{ search }">
          {{ search ? 'Nenhuma situação corresponde à busca.' : 'Nenhuma situação disponível.' }}
        </template>
      </v-select>
      <small class="status-caption">Selecione uma situação; os demais filtros são mantidos.</small>
    </div>

    <div v-if="showBackorders" class="backorders-control">
      <button
        type="button"
        class="backorders-button"
        :class="{ 'is-active': backordersSelected }"
        :aria-pressed="backordersSelected"
        :disabled="disabled"
        @click="$emit('backorders')"
      >
        <feather-icon icon="LayersIcon" size="16" aria-hidden="true" />
        <span>Back Orders</span>
        <span :aria-busy="backordersLoading" aria-live="polite">
          <span v-if="backordersLoading" class="status-skeleton" aria-label="Carregando contagem"></span>
          <strong v-else>{{ backordersError ? '—' : formatCount(backordersCount) }}</strong>
        </span>
      </button>
      <small class="status-caption">Total conforme os demais filtros</small>
      <div v-if="backordersError" class="status-error" role="alert">
        {{ backordersError }}
        <button type="button" class="status-retry" :disabled="backordersLoading" @click="$emit('retry-backorders')">
          Tentar novamente
        </button>
      </div>
    </div>

    <div v-if="selectedStatus || backordersSelected" class="active-status" aria-live="polite">
      <span>Filtro ativo: <strong>{{ backordersSelected ? 'Back Orders' : selectedStatus }}</strong></span>
      <button
        type="button"
        class="status-clear"
        :disabled="disabled"
        :aria-label="backordersSelected ? 'Remover filtro Back Orders' : 'Remover filtro de situação'"
        @click="clearSelection"
      >
        <feather-icon icon="XIcon" size="14" aria-hidden="true" />
        <span>Remover</span>
      </button>
    </div>
    <div v-if="statusError" class="status-error summary-error" role="alert">
      {{ statusError }}
      <button type="button" class="status-retry" :disabled="statusLoading" @click="$emit('retry-status')">
        Tentar novamente
      </button>
    </div>
  </section>
</template>

<script>
import VSelect from 'vue-select';
import { pedidoCategory } from '@/utils/pedidoListingRows';

export default {
  name: 'KpiCards',
  components: { VSelect },
  props: {
    orders: { type: Array, default: () => [] },
    statusField: { type: String, default: '' },
    // null means no server summary; [] means a successful, empty server summary.
    statusSummary: { type: Array, default: null },
    statusOptions: { type: Array, default: () => [] },
    selectedStatus: { type: String, default: null },
    statusLoading: { type: Boolean, default: false },
    statusError: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    showBackorders: { type: Boolean, default: true },
    backordersSelected: { type: Boolean, default: false },
    backordersCount: { type: Number, default: null },
    backordersLoading: { type: Boolean, default: false },
    backordersError: { type: String, default: '' },
  },
  computed: {
    selectorId() {
      return `pedido-status-${this._uid}`;
    },
    summaryLabel() {
      const scope = this.statusSummary === null ? 'carregados' : 'encontrados';
      return `Pedidos ${scope}${this.showBackorders ? ' (sem Back Orders)' : ''}`;
    },
    summaryCount() {
      return this.computedKpis.reduce((total, item) => total + (item.count || 0), 0);
    },
    computedKpis() {
      const counts = new Map();
      const add = (raw, total) => {
        const status = raw === null || raw === undefined || raw === '' ? 'SEM STATUS' : String(raw);
        if (this.showBackorders && this.isBackorder(status)) return;
        const count = Number(total);
        counts.set(status, (counts.get(status) || 0) + (Number.isFinite(count) && count >= 0 ? count : 0));
      };
      if (this.statusSummary !== null) {
        this.statusSummary.forEach(item => add(
          item.pedidositcomercial ?? item.pedidoSitComercial ?? item.status,
          item.Total ?? item.total ?? 0,
        ));
      } else {
        this.orders.forEach(order => {
          const category = pedidoCategory(order);
          add(this.statusField && !this.isBackorder(category) ? order[this.statusField] : category, 1);
        });
      }
      const options = new Map();
      counts.forEach((count, status) => options.set(status, { status, label: status, count }));
      this.statusOptions.forEach(option => {
        const value = typeof option === 'string' ? option : option.value;
        if (value === null || value === undefined || value === '') return;
        const status = String(value);
        if (this.showBackorders && this.isBackorder(status)) return;
        options.set(status, {
          status,
          label: typeof option === 'string' ? status : String(option.text || status),
          count: counts.has(status) ? counts.get(status) : null,
          disabled: Boolean(option.disabled),
        });
      });
      // Keep a selected value visible even after an empty response or a refresh.
      if (this.selectedStatus && !options.has(this.selectedStatus)
        && !(this.showBackorders && this.isBackorder(this.selectedStatus))) {
        options.set(this.selectedStatus, { status: this.selectedStatus, label: this.selectedStatus, count: null });
      }
      return Array.from(options.values()).sort((a, b) => (
        (b.count || 0) - (a.count || 0) || a.label.localeCompare(b.label, 'pt-BR')
      ));
    },
    activeOption() {
      return this.backordersSelected ? null : this.computedKpis.find(item => item.status === this.selectedStatus) || null;
    },
  },
  methods: {
    isBackorder(status) {
      return String(status).trim().toUpperCase() === 'BACK ORDER';
    },
    formatCount(count) {
      return count === null ? '—' : new Intl.NumberFormat('pt-BR').format(count);
    },
    searchOptions(options, search) {
      const normalize = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const query = normalize(search).trim();
      return options.filter(option => normalize(`${option.label} ${option.status}`).includes(query));
    },
    selectOption(option) {
      if (!this.disabled && option && !option.disabled) {
        this.$emit('filter', !this.backordersSelected && this.selectedStatus === option.status ? null : option.status);
      }
    },
    isSelectable(option) {
      return !option.disabled;
    },
    clearSelection() {
      if (this.disabled) return;
      if (this.backordersSelected) this.$emit('backorders');
      else this.$emit('filter', null);
    },
  },
};
</script>

<style scoped>
.status-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.25rem;
  padding: 0.875rem 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid var(--sf-card-border, #d8dee8);
  border-radius: 8px;
  background: var(--sf-card-bg, #f7f9fc);
  color: var(--sf-text-primary, #25354b);
}
.status-summary { display: flex; flex-direction: column; flex: 0 1 240px; }
.status-total { font-size: 1.5rem; line-height: 1.3; font-variant-numeric: tabular-nums; }
.status-caption, .status-summary small { color: var(--sf-text-secondary, #55657b); font-size: 0.75rem; }
.status-selector { flex: 1 1 280px; min-width: 0; }
.status-selector label { display: block; font-weight: 600; margin-bottom: 0.25rem; color: inherit; }
.status-option { display: flex; align-items: center; justify-content: space-between; gap: 1rem; white-space: normal; overflow-wrap: anywhere; }
.status-option small { flex-shrink: 0; font-variant-numeric: tabular-nums; }
.backorders-control { flex: 0 1 250px; display: flex; flex-direction: column; gap: 0.25rem; }
.backorders-button, .status-clear {
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  border: 1px solid var(--sf-card-border, #d8dee8); border-radius: 6px;
  padding: 0.55rem 0.75rem; min-height: 38px;
  background: var(--sf-card-bg, #f7f9fc); color: inherit; cursor: pointer;
}
.backorders-button.is-active { border-color: var(--sf-accent, #5264b5); background: var(--sf-accent-bg, #edf0fa); }
.backorders-button strong { font-variant-numeric: tabular-nums; }
.active-status { display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem 1rem; flex-basis: 100%; font-size: 0.8rem; overflow-wrap: anywhere; }
.status-clear { padding: 0.25rem 0.5rem; font-size: 0.8rem; }
.status-error { color: var(--sf-danger, #c34b54); font-size: 0.8rem; overflow-wrap: anywhere; }
.summary-error { flex-basis: 100%; }
.status-retry { background: transparent; color: inherit; border: 0; text-decoration: underline; padding: 0.25rem; cursor: pointer; }
button:focus-visible { outline: 2px solid var(--sf-accent, #5264b5); outline-offset: 3px; }
button:disabled { opacity: 0.6; cursor: not-allowed; }
.status-skeleton { display: inline-block; height: 1rem; width: 3rem; border-radius: 4px; background: var(--sf-card-border, #d8dee8); }
.status-selector ::v-deep .vs__dropdown-toggle { background: var(--sf-card-bg, #f7f9fc); border-color: var(--sf-card-border, #d8dee8); }
.status-selector ::v-deep .vs__selected, .status-selector ::v-deep .vs__search { color: var(--sf-text-primary, #25354b); }
.status-selector ::v-deep .vs__selected { white-space: normal; overflow-wrap: anywhere; }
.status-selector ::v-deep .vs__open-indicator { fill: var(--sf-text-secondary, #55657b); }
.status-selector ::v-deep .vs__dropdown-menu { max-height: 260px; background: var(--sf-card-bg, #f7f9fc); color: var(--sf-text-primary, #25354b); border-color: var(--sf-card-border, #d8dee8); }
.status-selector ::v-deep .vs__dropdown-option { color: inherit; padding-top: 0.5rem; padding-bottom: 0.5rem; }
.status-selector ::v-deep .vs__dropdown-option--highlight { background: var(--sf-accent-bg, #edf0fa); color: var(--sf-text-primary, #25354b); }
.status-selector ::v-deep .vs--open .vs__dropdown-toggle { outline: 2px solid var(--sf-accent, #5264b5); outline-offset: 1px; }
@media (max-width: 767px) {
  .status-toolbar { align-items: stretch; gap: 0.75rem; padding: 0.75rem; }
  .status-summary, .status-selector, .backorders-control { flex-basis: 100%; }
  .status-summary { flex-direction: row; align-items: baseline; flex-wrap: wrap; gap: 0.25rem 0.5rem; }
  .status-summary .status-caption { flex-basis: 100%; }
}
</style>